import styled, { DefaultTheme } from 'styled-components';
import { Card, Button, Tabs, Modal, Typography } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T =>
  props.theme?.mode === 'dark' ? darkValue : lightValue;

export const Container = styled.div`
  width: 100%;
  padding: 24px;
  background: ${(props) => getThemeValue(props, '#0d1117', '#f5f5f5')};

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const Title = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 700 !important;
  letter-spacing: -0.5px;
`;

export const PrimaryButton = styled(Button)`
  background: #2d6cca !important;
  border-color: #2d6cca !important;
  color: white !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  transition: all 0.2s;

  &:hover {
    background: #2560b8 !important;
    border-color: #2560b8 !important;
    color: white !important;
  }
`;

export const ContentCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  box-shadow: ${(props) =>
    getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};

  .ant-card-body {
    padding: 16px;
  }
`;

export const SectionCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  margin-top: 16px;
  margin-bottom: 24px;

  .ant-card-body {
    padding: 0;
  }
`;

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;

export const SectionTitle = styled(Typography.Title)`
  margin: 0 !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  letter-spacing: -0.5px;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
`;

export const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 16px;
  }
  .ant-tabs-tab {
    color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
  }
  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  }
  .ant-tabs-ink-bar {
    background: #2d6cca;
  }
  .ant-tabs-content-holder {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  }
`;

export const StatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-start;
`;

export const StatCard = styled.div`
  padding: 16px;
  border-radius: 8px;
  background: ${(props) => getThemeValue(props, '#21262d', '#fafafa')};
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  min-width: 160px;
`;

export const StatLabel = styled.div`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const StatValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
`;

export const StatHelp = styled.div`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
  margin-top: 4px;
`;

export const EmptyStateBox = styled.div`
  height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  border-radius: 12px;
  background: ${(props) => getThemeValue(props, '#21262d', '#fafafa')};
`;

export const EmptyStateIconWrap = styled.span`
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
`;

export const EmptyStateText = styled.span`
  margin-top: 12px;
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
`;

export const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
    border-radius: 12px;
  }
  .ant-modal-header {
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  }
  .ant-modal-title {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  }
  .ant-modal-body {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  }
  .ant-modal-footer {
    background: ${(props) => getThemeValue(props, '#21262d', '#fafafa')};
    border-top: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  }
`;

export const ModalFooterWrap = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const DoneButton = styled(Button)`
  background: #1a1a1a !important;
  border-color: #1a1a1a !important;
  color: white !important;
  border-radius: 8px !important;

  &:hover {
    background: #333 !important;
    border-color: #333 !important;
    color: white !important;
  }
`;

export const GlossaryTabTitle = styled.span`
  font-size: 14px;
`;

export const GlossaryParagraph = styled.p`
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 1.5;
  color: ${(props) => getThemeValue(props, '#c9d1d9', '#333')};
`;

export const GlossaryHeading = styled.strong`
  display: block;
  margin-top: 12px;
  margin-bottom: 4px;
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
`;

export const InfoIconButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
  &:hover {
    color: ${(props) => getThemeValue(props, '#2d6cca', '#2d6cca')};
  }
`;
